import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { Alert, Popconfirm, Select, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined } from "@ant-design/icons";

import { Button } from "../../shared";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { convertToUserTimezone } from "../../../utils/dateService";
import { isPermissionBypassActive } from "../../../utils/permissionBypass";
import {
  CASINO_SCHEDULED_TASK_PURPOSES,
  isScheduledTaskTerminal,
  useCasinoScheduledTaskRun,
  useCasinoScheduledTaskRuns,
  useTriggerCasinoScheduledTask,
} from "../../../queries/casino/useCasinoScheduledTasks";

import "./stylesScheduledTasks.css";

const EMPTY_DISPLAY = "—";
const ERROR_EXCERPT_MAX = 80;

export type ScheduledTaskRun = {
  run_id: string;
  purpose: string;
  status: string;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  result?: string | null;
  result_truncated?: boolean;
  error?: string | null;
  error_truncated?: boolean;
  correlation_id?: string | null;
};

type AppContextShape = {
  adminPermissions?: string[] | null;
  setActiveSidebarTab?: (tab: string, option?: string) => void;
};

// AppStrings keys (values are read at render time — TCM overrides the
// defaults after module load, so no `AppStrings.*` at module scope).
const PURPOSE_LABEL_KEYS: Record<string, string> = {
  "generate-betcore-access-token":
    "CASINO_SCHEDULED_TASKS_PURPOSE_GENERATE_BETCORE_ACCESS_TOKEN",
  "load-casino-games": "CASINO_SCHEDULED_TASKS_PURPOSE_LOAD_CASINO_GAMES",
  "overwrite-casino-game-images":
    "CASINO_SCHEDULED_TASKS_PURPOSE_OVERWRITE_CASINO_GAME_IMAGES",
  "overwrite-casino-game-images-force-refresh":
    "CASINO_SCHEDULED_TASKS_PURPOSE_OVERWRITE_CASINO_GAME_IMAGES_FORCE_REFRESH",
  "create-default-casino-entities":
    "CASINO_SCHEDULED_TASKS_PURPOSE_CREATE_DEFAULT_CASINO_ENTITIES",
};

const STATUS_META: Record<string, { color: string; labelKey: string }> = {
  pending: { color: "gold", labelKey: "CASINO_SCHEDULED_TASKS_STATUS_PENDING" },
  running: {
    color: "processing",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_RUNNING",
  },
  success: {
    color: "success",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_SUCCESS",
  },
  failed: { color: "error", labelKey: "CASINO_SCHEDULED_TASKS_STATUS_FAILED" },
  orphaned: {
    color: "warning",
    labelKey: "CASINO_SCHEDULED_TASKS_STATUS_ORPHANED",
  },
};

export function purposeLabel(purpose: string): string {
  const key = PURPOSE_LABEL_KEYS[purpose];
  return (key && AppStrings[key]) || purpose;
}

function statusTag(status?: string | null): ReactElement {
  const meta = STATUS_META[status ?? ""];
  return (
    <Tag color={meta?.color ?? "default"}>
      {meta ? AppStrings[meta.labelKey] : (status ?? EMPTY_DISPLAY)}
    </Tag>
  );
}

function formatRunDate(iso?: string | null): string {
  return iso ? convertToUserTimezone(iso, "YYYY-MM-DD HH:mm:ss") : EMPTY_DISPLAY;
}

export function errorExcerpt(text?: string | null): string {
  if (!text) {
    return EMPTY_DISPLAY;
  }
  return text.length > ERROR_EXCERPT_MAX
    ? `${text.slice(0, ERROR_EXCERPT_MAX)}…`
    : text;
}

/** Friendly copy for known trigger failures, keyed on `msg_code` (never prose). */
export function triggerErrorCopy(error: unknown): string {
  const msgCode = (
    error as { response?: { data?: { msg_code?: string } } } | null
  )?.response?.data?.msg_code;
  switch (msgCode) {
    case "casino.error.scheduled_task_purpose_not_allowed":
      return AppStrings.CASINO_SCHEDULED_TASKS_PURPOSE_NOT_ALLOWED;
    case "casino.error.scheduled_task_consumer_disabled":
      return AppStrings.CASINO_SCHEDULED_TASKS_CONSUMER_DISABLED;
    case "casino.error.scheduled_task_queue_unavailable":
      return AppStrings.CASINO_SCHEDULED_TASKS_QUEUE_UNAVAILABLE;
    default:
      return AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_ERROR;
  }
}

function getColumns(): ColumnsType<ScheduledTaskRun> {
  return [
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_STATUS,
      dataIndex: "status",
      key: "status",
      render: (status: string) => statusTag(status),
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_CREATED,
      dataIndex: "created_at",
      key: "created_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_STARTED,
      dataIndex: "started_at",
      key: "started_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_FINISHED,
      dataIndex: "finished_at",
      key: "finished_at",
      render: formatRunDate,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_CORRELATION_ID,
      dataIndex: "correlation_id",
      key: "correlation_id",
      render: (id?: string | null) => id ?? EMPTY_DISPLAY,
    },
    {
      title: AppStrings.CASINO_SCHEDULED_TASKS_COL_ERROR,
      dataIndex: "error",
      key: "error",
      render: (error?: string | null) => (
        <span title={error ?? undefined}>{errorExcerpt(error)}</span>
      ),
    },
  ];
}

function ScheduledTasks() {
  usePageTitle(AppStrings.CASINO_SCHEDULED_TASKS);
  const { adminPermissions, setActiveSidebarTab } = (useContext(AppContext) ??
    {}) as AppContextShape;

  const [purpose, setPurpose] = useState<string>(
    CASINO_SCHEDULED_TASK_PURPOSES[0],
  );
  // `startedAt` bounds the run poll to the client cap (see
  // `scheduledTaskPollInterval`) — same shape as CasinoGamesSchedulerMenu.
  const [tracked, setTracked] = useState<{
    correlationId: string;
    startedAt: number;
  } | null>(null);
  const trackedCorrelationId = tracked?.correlationId ?? null;

  const canTrigger =
    isPermissionBypassActive() ||
    Boolean(adminPermissions?.includes("casino.scheduled_tasks.trigger"));

  const runsQuery = useCasinoScheduledTaskRuns(purpose);
  const triggerMutation = useTriggerCasinoScheduledTask();
  const runQuery = useCasinoScheduledTaskRun(
    trackedCorrelationId,
    tracked?.startedAt ?? 0,
  );

  useEffect(() => {
    setActiveSidebarTab?.(SIDER_TABS.CONFIGURATIONS, "CASINO_SCHEDULED_TASKS");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (runsQuery.isError) {
      message.error(AppStrings.CASINO_SCHEDULED_TASKS_LOAD_ERROR);
    }
  }, [runsQuery.isError]);

  // Once the tracked run lands on a terminal status the poll has stopped —
  // refresh the history table so the final state shows there too.
  const trackedStatus = runQuery.data?.status;
  const refetchRuns = runsQuery.refetch;
  useEffect(() => {
    if (trackedCorrelationId && isScheduledTaskTerminal(trackedStatus)) {
      refetchRuns();
    }
  }, [trackedCorrelationId, trackedStatus, refetchRuns]);

  const handlePurposeChange = useCallback((value: string) => {
    setPurpose(value);
    setTracked(null);
  }, []);

  const handleTrigger = useCallback(() => {
    triggerMutation.mutate(purpose, {
      onSuccess: (data: { correlation_id?: string | null } | null) => {
        message.success(AppStrings.CASINO_SCHEDULED_TASKS_TRIGGERED);
        const correlationId = data?.correlation_id ?? null;
        setTracked(
          correlationId ? { correlationId, startedAt: Date.now() } : null,
        );
        refetchRuns();
      },
      onError: (error: unknown) => {
        message.error(triggerErrorCopy(error));
      },
    });
  }, [purpose, refetchRuns, triggerMutation]);

  const runs: ScheduledTaskRun[] = runsQuery.data?.runs ?? [];
  const bannerType =
    trackedStatus === "success"
      ? "success"
      : trackedStatus === "failed" || trackedStatus === "orphaned"
        ? "error"
        : "info";

  return (
    <>
      <div className="casino-scheduled-tasks-page">
        <div className="casino-scheduled-tasks-controls">
          <Select
            className="casino-scheduled-tasks-purpose-select"
            aria-label={AppStrings.CASINO_SCHEDULED_TASKS_PURPOSE_LABEL}
            value={purpose}
            onChange={handlePurposeChange}
            options={CASINO_SCHEDULED_TASK_PURPOSES.map((p) => ({
              value: p,
              label: purposeLabel(p),
            }))}
          />
          {canTrigger ? (
            <Popconfirm
              title={AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_CONFIRM_TITLE}
              description={formatString(
                AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER_CONFIRM_DESCRIPTION,
                { purpose: purposeLabel(purpose) },
              )}
              onConfirm={handleTrigger}
              okText={AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER}
              cancelText={AppStrings.CANCEL}
            >
              <Button intent="primary" loading={triggerMutation.isPending}>
                {AppStrings.CASINO_SCHEDULED_TASKS_TRIGGER}
              </Button>
            </Popconfirm>
          ) : null}
          <Button
            intent="secondary"
            iconLeft={<ReloadOutlined />}
            loading={runsQuery.isFetching}
            onClick={() => refetchRuns()}
          >
            {AppStrings.CASINO_SCHEDULED_TASKS_REFRESH}
          </Button>
        </div>

        {trackedCorrelationId ? (
          <Alert
            className="casino-scheduled-tasks-run-banner"
            type={bannerType}
            showIcon
            message={
              <span>
                {AppStrings.CASINO_SCHEDULED_TASKS_LAST_RUN}:{" "}
                {statusTag(trackedStatus)}
                <code>{trackedCorrelationId}</code>
              </span>
            }
          />
        ) : null}

        <Table<ScheduledTaskRun>
          rowKey="run_id"
          columns={getColumns()}
          dataSource={runs}
          loading={runsQuery.isFetching}
          pagination={false}
          size="middle"
          scroll={{ x: true }}
        />
      </div>
    </>
  );
}

export default ScheduledTasks;
